import { BaseElement } from '../../../base-element';
import type { ValidationError } from '../../../../state/form.state';

export interface ValidationSummaryItem {
  path: string;
  message: string;
}

export const fieldLabels: Record<string, string> = {
  'dog.ascaRegistrationNumber': 'ASCA Registration #',
  'dog.breed': 'Breed',
  'dog.registeredName': 'Registered Name',
  'dog.callName': 'Call Name',
  'dog.dob': 'Date of Birth',
  'dog.sex': 'Sex',
  'contact.owners': 'Owner(s)',
  'contact.ownerAddress.street': 'Street',
  'contact.ownerAddress.city': 'City',
  'contact.ownerAddress.state': 'State',
  'contact.ownerAddress.zip': 'ZIP',
  'contact.email': 'Email',
  'contact.phone': 'Phone',
  'emergencyContact.name': 'Emergency contact name',
  'emergencyContact.phoneOrNumber': 'Emergency contact number',
  'fees.totalEntryFees': 'Total entry fees',
  selections: 'Class selections',
  'terms.accepted': 'Terms and conditions',
};

export function buildErrorMessage(
  path: string,
  code?: string,
  message?: string
): string {
  if (message) {
    return message;
  }

  const label = fieldLabels[path] ?? 'This field';

  switch (code) {
    case 'required':
      return `${label} is required.`;
    case 'email':
      return 'Enter a valid email.';
    case 'pattern':
      if (path === 'contact.ownerAddress.zip') {
        return 'Enter a valid ZIP code.';
      }
      if (path === 'contact.phone' || path === 'emergencyContact.phoneOrNumber') {
        return 'Enter a phone number in 555-555-5555 format.';
      }
      return `${label} has an invalid format.`;
    case 'min':
      if (path === 'fees.totalEntryFees') {
        return 'Total entry fees must be greater than $0.00.';
      }
      return `${label} is too low.`;
    case 'minSelections':
      return 'Select at least one class in the grid.';
    case 'pastDate':
      return 'Date of Birth must be in the past.';
    case 'terms':
      return 'You must accept the terms before submitting.';
    default:
      return `${label} is invalid.`;
  }
}

export function collectErrors(errors: ValidationError[]): ValidationSummaryItem[] {
  return errors.map((error) => ({
    path: error.field,
    message: buildErrorMessage(error.field, error.code, error.message),
  }));
}

export class ValidationSummary extends BaseElement {
  private _errors: ValidationError[] = [];
  private _visible = false;
  private _collapsed = false;

  get errors(): ValidationError[] {
    return this._errors;
  }

  set errors(value: ValidationError[]) {
    this._errors = value ?? [];
    this.updateRender();
  }

  get visible(): boolean {
    return this._visible;
  }

  set visible(value: boolean) {
    this._visible = value;
    this.updateRender();
  }

  connectedCallback(): void {
    this.updateRender();
  }

  private handleErrorClick = (path: string): void => {
    this.emit('error-selected', path);
  };

  private toggleCollapse = (): void => {
    this._collapsed = !this._collapsed;
    this.updateRender();
  };

  private updateRender(): void {
    const items = collectErrors(this._errors);

    if (!this._visible || items.length === 0) {
      this.render('');
      return;
    }

    const collapsedHtml = this._collapsed ? `
      <section
        class="sticky top-0 z-30 border-b border-error-200 bg-error-50 px-4 py-2 shadow-md md:top-16 md:border md:rounded-xl md:p-4 md:shadow-lg"
        class="fixed bottom-0 left-0 right-0 z-30 border-t border-error-200 bg-error-50 px-4 py-2 pb-[env(safe-area-inset-bottom)] shadow-lg md:static md:border md:rounded-xl md:p-4"
        data-testid="validation-summary-content"
        role="alert"
        aria-live="assertive"
      >
        <button type="button" class="flex w-full items-center gap-2 text-sm font-semibold text-error-700" data-testid="validation-toggle">
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd"/>
          </svg>
          <span>${items.length} error${items.length !== 1 ? 's' : ''}</span>
          <svg class="ml-auto h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M12.53 16.28a.75.75 0 01-1.06 0l-7.5-7.5a.75.75 0 011.06-1.06L12 14.69l6.97-6.97a.75.75 0 111.06 1.06l-7.5 7.5z" clip-rule="evenodd"/>
          </svg>
        </button>
      </section>
    ` : '';

    if (this._collapsed) {
      this.render(collapsedHtml);
      const toggle = this.$('[data-testid="validation-toggle"]');
      if (toggle) toggle.addEventListener('click', this.toggleCollapse);
      return;
    }

    this.render(`
      <section
        class="sticky top-0 z-30 rounded-xl border border-error-200 bg-error-50 p-4 shadow-lg md:top-16"
        data-testid="validation-summary-content"
        role="alert"
        aria-live="assertive"
      >
        <div class="flex items-start gap-3">
          <div class="mt-1 text-error-600">
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="flex-1">
            <div class="flex items-center justify-between">
              <div>
                <div class="text-xs font-semibold uppercase tracking-widest text-error-700">Validation summary</div>
                <div class="text-sm text-error-700">Please fix the following errors:</div>
              </div>
              <button type="button" class="text-xs font-medium text-error-600 hover:text-error-700" data-testid="validation-toggle">
                ${items.length} error${items.length !== 1 ? 's' : ''} — collapse
              </button>
            </div>
          </div>
        </div>
        <ul class="mt-3 max-h-48 space-y-2 overflow-y-auto" role="list">
          ${items
            .map(
              (item) => `
            <li>
              <button type="button" class="flex w-full items-center gap-2 text-left text-sm text-error-700 hover:text-error-800" data-error-path="${item.path}" tabindex="0">
                <span class="text-error-600">•</span>
                <span class="flex-1">${item.message}</span>
                <svg class="h-4 w-4 text-error-500" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M12.97 3.97a.75.75 0 011.06 0l7.5 7.5a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 11-1.06-1.06l6.22-6.22H3a.75.75 0 010-1.5h16.19l-6.22-6.22a.75.75 0 010-1.06z" clip-rule="evenodd"/>
                </svg>
              </button>
            </li>
          `
            )
            .join('')}
        </ul>
      </section>
    `);

    const toggle = this.$('[data-testid="validation-toggle"]');
    if (toggle) toggle.addEventListener('click', this.toggleCollapse);

    this.$$('button[data-error-path]').forEach((button) => {
      const path = button.getAttribute('data-error-path');
      if (!path) return;
      button.addEventListener('click', () => this.handleErrorClick(path));
    });
  }
}

customElements.define('dt-validation-summary', ValidationSummary);
